import type { IntentConfig, ResolvedGuardrails } from "@fathom/intent";
import type { ProjectionContext } from "../types.js";

/**
 * Generate a generic system prompt suitable for any LLM API call.
 *
 * More concise than the other projections — focused on the essential
 * context and hard rules an LLM needs to follow.
 */
export function generateSystemPrompt(
  intent: IntentConfig,
  guardrails: ResolvedGuardrails,
  context?: ProjectionContext,
): string {
  const parts: string[] = [];

  // Identity + context
  parts.push(
    `You are an AI assistant working on ${intent.project}. ${intent.description}`,
  );
  if (intent.audience) {
    parts.push(`The target audience is: ${intent.audience}.`);
  }

  // Values — keep concise
  if (intent.values.length > 0) {
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const sorted = [...intent.values].sort(
      (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority],
    );
    const valueList = sorted
      .map((v) => {
        const tag = v.priority === "critical" ? " (CRITICAL)" : "";
        return `${v.name}${tag}: ${v.description}`;
      })
      .join("; ");
    parts.push(`Core values: ${valueList}.`);
  }

  // Budget — one line
  if (intent.budget) {
    let budgetLine = `Budget: ${intent.budget.currency} ${intent.budget.monthly_limit.toLocaleString()}/month.`;
    if (context?.trackingSummary) {
      const pct = Math.round(
        (context.trackingSummary.monthlySpend / intent.budget.monthly_limit) *
          100,
      );
      budgetLine += ` Current spend: ${pct}%.`;
    }
    if (intent.budget.prefer_batch) {
      budgetLine += " Prefer batch operations.";
    }
    parts.push(budgetLine);
  }

  // Guardrails — mandatory rules
  const allRules = [
    ...guardrails.security,
    ...guardrails.quality,
    ...guardrails.process,
  ];
  if (allRules.length > 0) {
    parts.push("Mandatory rules:");
    for (const rule of allRules) {
      parts.push(`- ${rule}`);
    }
  }

  // Tech — one line
  if (intent.tech) {
    const { stack, opinions } = intent.tech;
    if (stack.length > 0) {
      parts.push(`Tech stack: ${stack.join(", ")}.`);
    }
    if (opinions.length > 0) {
      parts.push(`Conventions: ${opinions.join("; ")}.`);
    }
  }

  return parts.join("\n") + "\n";
}
